interface CropBoxData {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface ImageDimensions {
  width: number;
  height: number;
}

/**
 * Calculates the dimensions and position of a centered crop box
 * for a given image and target aspect ratio.
 * This is useful for creating a default, sensible crop when an image is first loaded.
 *
 * @param imageDimensions - The dimensions of the original image.
 * @param targetRatio - The desired aspect ratio of the crop (e.g., 1 for square, 1.91 for landscape).
 * @returns An object containing the crop box's left, top, width, and height.
 */
export const calculateCenteredCrop = (imageDimensions: ImageDimensions, targetRatio: number): CropBoxData => {
  const { width: imageWidth, height: imageHeight } = imageDimensions;

  if (!imageHeight || !imageWidth || !targetRatio) {
    return { left: 0, top: 0, width: 0, height: 0 };
  }

  const imageRatio: number = imageWidth / imageHeight;

  let finalCropWidth: number, finalCropHeight: number, finalCropLeft: number, finalCropTop: number;

  if (imageRatio > targetRatio) {
    finalCropHeight = imageHeight;
    finalCropWidth = imageHeight * targetRatio;
    finalCropTop = 0;
    finalCropLeft = (imageWidth - finalCropWidth) / 2;
  } else {
    finalCropWidth = imageWidth;
    finalCropHeight = imageWidth / targetRatio;
    finalCropLeft = 0;
    finalCropTop = (imageHeight - finalCropHeight) / 2;
  }

  return {
    left: Math.round(finalCropLeft),
    top: Math.round(finalCropTop),
    width: Math.round(finalCropWidth),
    height: Math.round(finalCropHeight),
  };
};

/**
 * Converts a Blob object into a base64 Data URL string.
 * This is useful for storing image data in a serializable format like Redux state.
 *
 * @param blob - The Blob object to convert.
 * @returns A Promise that resolves with the Data URL string.
 */
export const blobToDataURL = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(reader.result as string);
    };

    reader.onerror = () => {
      reject(reader.error);
    };

    reader.readAsDataURL(blob);
  });
};

export const dataURLtoBlob = async (dataUrl: string): Promise<Blob> => {
  try {
    const res = await fetch(dataUrl);
    if (!res.ok) {
      throw new Error(`Failed to fetch data URL: ${res.status}`);
    }
    return res.blob();
  } catch (err) {
    const error = err as Error;
    throw new Error(`Failed to convert data URL to blob: ${error.message}`);
  }
};

export const getImageDimensions = (dataUrl: string): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = (err) => {
      reject(new Error('Failed to load image from Data URL'));
    };
    img.src = dataUrl;
  });
};
